/**
 * @module utils/filter
 */
import { isEqual } from 'lodash'
import { hasAccess } from './auth'
import { renderDateTime } from './time'

/**
 * @description Checks if filter is changed
 */
export function isFilterChanged (authObject, simplesearch, filter, initialFilter) {
  // console.log('IsFilterChanged: simple:', simplesearch, 'filter: ', filter, 'initial:', initialFilter)
  if (simplesearch !== undefined && simplesearch !== null && simplesearch !== '') {
    return true
  }
  let filterNames = [
    'color',
    'customer',
    'delivery',
    'evaluation',
    'external_control',
    'invoice',
    'invoicing',
    'movedOrders',
    'orderType',
    'parts',
    'process',
    'update',
    'vehicle',
    'customergroup_customergroupid',
    'pricemodel_pricemodelid',
    'HasPaintWork',
    'HasBodyWork',
    'NewOrdersFilter',
    'AssignedToMe'
  ]
  let foundIndex = -1
  for (let i = 0; i < filterNames.length; i++) {
    if (isFilterNameChanged(authObject, filterNames[i], filter, initialFilter)) {
      foundIndex = i
      break
    }
  }
  return foundIndex > -1
}
export function isFilterNameChanged (authObject, name, filter, initialFilter) {
  if (filter === null) return false
  if (hasAccess([4], authObject.user.UserType)) {
    initialFilter.process.Completed = false
    initialFilter.process.CompletedThisWeek = false
  } else {
    initialFilter.process.Completed = false
    // initialFilter.process.CompletedThisWeek = true  // Should be set in reducer state
  }
  if (hasAccess([6], authObject.user.UserType)) {
    initialFilter.process.draft1 = false
    initialFilter.process.draft2 = false
    initialFilter.process.draft = false
  }
  let checker = filter[name]
  if (typeof checker === 'object') {
    let checkState = JSON.parse(JSON.stringify(initialFilter[name]))
    if (authObject.user.CustomerId !== '-1') {
      if (typeof checkState['external_control'] !== 'undefined' || typeof checker['external_control'] !== 'undefined') {
        delete checkState['external_control']
        delete checker['external_control']
      }
      if (typeof checkState['invoicing'] !== 'undefined' || typeof checker['invoicing'] !== 'undefined') {
        delete checkState['invoicing']
        delete checker['invoicing']
      }
      if (typeof checkState['customer'] !== 'undefined' || typeof checker['customer'] !== 'undefined') {
        delete checkState['customer']
        delete checker['customer']
      }
      if (name === 'update') {
        delete checkState['missing']
        delete checker['missing']
      }
      if (name === 'delivery') {
        delete checkState['production']
        delete checker['production']
      }
      if (name === 'color') {
        delete checkState['amount']
        delete checker['amount']
      }
      if (typeof checkState['customergroup_customergroupid'] !== 'undefined' || typeof checker['customergroup_customergroupid'] !== 'undefined') {
        delete checkState['customergroup_customergroupid']
        delete checker['customergroup_customergroupid']
      }
      if (typeof checkState['pricemodel_pricemodelid'] !== 'undefined' || typeof checker['pricemodel_pricemodelid'] !== 'undefined') {
        delete checkState['pricemodel_pricemodelid']
        delete checker['pricemodel_pricemodelid']
      }
    }
    return !isEqual(checkState, checker)
  } else {
    let checkState = initialFilter[name]
    if (name === 'orderType' || name === 'HasBodyWork' || name === 'HasPaintWork' || name === 'NewOrdersFilter') {
      return checker !== checkState
    }
    if (filter !== undefined && ((filter.customergroup !== undefined && filter.customergroup.CustomerGroupId !== -1) || (filter.pricemodel !== undefined && filter.pricemodel.PriceModelId !== -1))) return true
    return !checker === checkState
  }
}

/**
 * @description Checks if value is empty and pushes it to array
 * @param  {String} value Value to check
 * @param  {Array} array Array to push to
 * @return {Array} Returns array
 */
function shouldPushToArray (value, array) {
  if (value === '') {
    return array
  }
  array.push(value)
  return array
}

/**
 * @description Makes a contains string
 * @param  {String} text Filter request name
 * @param  {String} value Value to search for
 * @param  {Integer} option Include, Empty, Filled, Exclude
 * @return {String} Returns a contains string
 */
export function makeContains (text, value, option) {
  if (option === 1 || option === 4) {
    if (text === '') {
      return ''
    }
  }
  let testing = ''
  switch (option) {
    case 2:
      return '(' + value + ' eq null)'
    case 3:
      return '(' + value + ' ne null)'
    case 4:
      testing = ' eq false'
      break
  }
  return '(contains(tolower(' + value + '), tolower(\'' + text + '\'))' + testing + ')'
}

/**
 * @description Makes a greater/less than string
 * @param  {Object} data Object containing start/end
 * @param  {String} freq Filter request name
 * @return {String} Returns a greater/less than string
 */
function makeGreaterLessThan (data, freq) {
  let wrapper = []
  if (data.start !== '') {
    if (renderDateTime(data.start, '00:00:00') !== '') {
      wrapper.push(freq + ' gt ' + renderDateTime(data.start, '00:00:00'))
    }
  }
  if (data.end !== '') {
    if (renderDateTime(data.end, '23:59:59') !== '') {
      wrapper.push(freq + ' lt ' + renderDateTime(data.end, '23:59:59'))
    }
  }
  if (wrapper.length !== 0) {
    return '(' + wrapper.join(' and ') + ')'
  } else {
    return ''
  }
}

/**
 * @description Advanced search
 * @param  {Object} data Object containing data to use for filtering
 * @return {String} Returns filtering string
 */
export function advancedSearch (data) {
  let arraySearch = []
  // WP Invoiced
  if (typeof data.wpinvoice !== 'undefined') {
    switch (data.wpinvoice) {
      case 'hasInvoiced':
        arraySearch.push('(HasInvoiced eq true)')
        break
      case 'hasNotInvoiced':
        arraySearch.push('(HasNotInvoiced eq true)')
        break
    }
  }
  // CustomerGroup
  if (typeof data.customerGroup !== 'undefined') {
    if (data.customerGroup.customerGroupId !== '0') {
      arraySearch.push('(CustomerGroupId eq \'' + data.customerGroup.customerGroupId + '\')')
    }
  }

  // PriceModel
  if (typeof data.priceModel !== 'undefined') {
    if (data.priceModel.priceModelId !== '0') {
      arraySearch.push('(PriceModelId eq \'' + data.priceModel.priceModelId + '\')')
    }
  }

  // OrderType
  if (typeof data.orderType !== 'undefined') {
    if (data.orderType !== '0') {
      arraySearch.push('(OrderType eq \'' + data.orderType + '\')')
    }
  }

  // Process step
  if (typeof data.process !== 'undefined') {
    switch (data.process.step) {
      case 'completed':
        arraySearch.push('(IsCompleted eq true)')
        break
      case 'inprocess':
        arraySearch.push('(IsInProcess eq true)')
        break
    }
  }

  // Evaluation

  // Update
  if (typeof data.update !== 'undefined') {
    if (data.update.updated) {
      arraySearch.push('(IsUpdated eq true)')
    }
  }

  // Identification
  if (typeof data.identification !== 'undefined') {
    if (filterIdentification(data.identification) !== '') {
      arraySearch.push(filterIdentification(data.identification))
    }
  }

  // Delivery
  if (typeof data.delivery !== 'undefined') {
    if (filterDelivery(data.delivery) !== '') {
      arraySearch.push(filterDelivery(data.delivery))
    }
  }

  // Vehicle data
  if (typeof data.vehicle !== 'undefined') {
    if (filterVehicle(data.vehicle) !== '') {
      arraySearch.push(filterVehicle(data.vehicle))
    }
  }

  // Color
  if (typeof data.color !== 'undefined') {
    if (filterColor(data.color) !== '') {
      arraySearch.push(filterColor(data.color))
    }
  }

  // Customer
  if (typeof data.customer !== 'undefined') {
    if (filterCustomer(data.customer) !== '') {
      arraySearch.push(filterCustomer(data.customer))
    }
  }

  let searchTerm = ''
  if (arraySearch.length !== 0) {
    searchTerm = arraySearch.join(' and ')
  }

  // Draft
  if (typeof data.process !== 'undefined') {
    if (data.process.draft || data.process.step === 'all') {
      let searchTerm = arraySearch.join(' and ')
      if (searchTerm !== '') {
        return '(IsDraft eq true) or (' + searchTerm + ')'
      } else {
        return searchTerm
      }
    }
  }
  return searchTerm
}

/**
 * @description Makes a string for filtering Odata orders
 * @param  {String} search Text to search for
 * @return {String} Returns transformed text
 */
export function filterSearch (search) {
  if (!search) return ''

  search = search.replace(/\s+/g, ' ')
  if (search && search !== ' ') {
    let arraySearch = []
    if (!isNaN(search)) {
      arraySearch.push('(Id eq ' + search + ')')
    }
    arraySearch.push(makeContains(search, 'RekvNumber', 1))
    arraySearch.push(makeContains(search, 'Color', 1))
    arraySearch.push(makeContains(search, 'ColorCode', 1))
    arraySearch.push(makeContains(search, 'ColorVariant', 1))
    arraySearch.push(makeContains(search, 'RegNumber', 1))
    arraySearch.push(makeContains(search, 'Model', 1))
    arraySearch.push(makeContains(search, 'BrandOriginal', 1))
    arraySearch.push(makeContains(search, 'ChassisNumber', 1))
    return arraySearch.join(' or ')
  } else {
    return ''
  }
}

/**
 * @description Makes a string for filtering customer
 * @param  {Object} customerData Object with data about customer value and options
 * @return {String} Returns filtering string
 */
export function filterCustomer (customerData) {
  if (Object.keys(customerData).length === 0) return ''

  let arraySearch = []

  // customerData.name
  if (typeof customerData.name !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(customerData.name.value, 'CustomerName', customerData.name.option), arraySearch)
  }

  // customerData.city
  if (typeof customerData.city !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(customerData.city.value, 'CustomerCity', customerData.city.option), arraySearch)
  }

  // customerData.zipcode
  if (typeof customerData.zipcode !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(customerData.zipcode.value, 'CustomerZip', customerData.zipcode.option), arraySearch)
  }

  // customerData.phone
  if (typeof customerData.phone !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(customerData.phone.value, 'CustomerPhone', customerData.phone.option), arraySearch)
  }

  // customerData.contact_name
  if (typeof customerData.contact_name !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(customerData.contact_name.value, 'Contact', customerData.contact_name.option), arraySearch)
  }

  if (arraySearch.length === 0) {
    return ''
  }
  return '(' + arraySearch.join(' and ') + ')'
}

/**
 * @description Makes a string for filtering color
 * @param  {Object} colorData Object with data about color value and options
 * @return {String} Returns filtering string
 */
export function filterColor (colorData) {
  if (Object.keys(colorData).length === 0) return ''

  let arraySearch = []

  // colorData.color
  if (typeof colorData.color !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(colorData.color.value, 'Color', colorData.color.option), arraySearch)
  }

  // colorData.code
  if (typeof colorData.code !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(colorData.code.value, 'ColorCode', colorData.code.option), arraySearch)
  }

  // colorData.ctype
  if (typeof colorData.ctype !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(colorData.ctype.value, 'ColorVariant', colorData.ctype.option), arraySearch)
  }

  // colorData.amount
  if (typeof colorData.amount !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(colorData.amount.value, 'ColorAmount', colorData.amount.option), arraySearch)
  }

  if (arraySearch.length === 0) {
    return ''
  }
  return '(' + arraySearch.join(' or ') + ')'
}

/**
 * @description Makes a string for filtering vehicle data
 * @param  {Object} vehicleData Object with data about vehicle data value and options
 * @return {String} Returns filtering string
 */
export function filterVehicle (vehicleData) {
  if (Object.keys(vehicleData).length === 0) return ''

  let arraySearch = []

  // vehicleData.regnumber
  if (typeof vehicleData.regnumber !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(vehicleData.regnumber.value, 'RegNumber', vehicleData.regnumber.option), arraySearch)
  }

  // vehicleData.brand
  if (typeof vehicleData.brand !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(vehicleData.brand.value, 'BrandOriginal', vehicleData.brand.option), arraySearch)
  }

  // vehicleData.model
  if (typeof vehicleData.model !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(vehicleData.model.value, 'Model', vehicleData.model.option), arraySearch)
  }

  // vehicleData.year
  if (typeof vehicleData.year !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(vehicleData.year.value, 'RegistrationDate', vehicleData.year.option), arraySearch)
  }

  if (arraySearch.length === 0) {
    return ''
  }
  return '(' + arraySearch.join(' or ') + ')'
}

/**
 * @description Makes a string for filtering delivery data
 * @param  {Object} deliveryData Object with data about delivery data value and options
 * @return {String} Returns filtering string
 */
export function filterDelivery (deliveryData) {
  if (Object.keys(deliveryData).length === 0) return ''
  let arraySearch = []

  // deliveryData.delivery
  if (typeof deliveryData.delivery !== 'undefined') {
    let delivery = makeGreaterLessThan(deliveryData.delivery, 'Delivery')
    arraySearch = shouldPushToArray(delivery, arraySearch)
  }

  // deliveryData.production
  if (typeof deliveryData.production !== 'undefined') {
    let production = makeGreaterLessThan(deliveryData.production, 'ProductionDate')
    arraySearch = shouldPushToArray(production, arraySearch)
  }

  // deliveryData.pickup
  if (typeof deliveryData.pickup !== 'undefined') {
    let pickup = makeGreaterLessThan(deliveryData.pickup, 'Pickup')
    arraySearch = shouldPushToArray(pickup, arraySearch)
  }

  // deliveryData.transport
  if (typeof deliveryData.transport !== 'undefined') {
    if (deliveryData.transport !== 0) {
      arraySearch.push('(Transport eq ' + deliveryData.transport + ')')
    }
  }

  if (arraySearch.length === 0) {
    return ''
  }
  return '(' + arraySearch.join(' and ') + ')'
}

/**
 * @description Makes a string for filtering identification data
 * @param  {Object} identificationData Object with data about identification data value and options
 * @return {String} Returns filtering string
 */
export function filterIdentification (identificationData) {
  if (Object.keys(identificationData).length === 0) return ''
  let arraySearch = []

  // identificationData.ordernumber
  if (typeof identificationData.ordernumber !== 'undefined') {
    if (identificationData.ordernumber.value !== '') {
      switch (identificationData.ordernumber.option) {
        case 1:
          arraySearch.push('(Id eq ' + identificationData.ordernumber.value + ')')
          break
        case 2:
          arraySearch.push('(Id eq null)')
          break
        case 3:
          arraySearch.push('(Id ne null)')
          break
        case 4:
          arraySearch.push('(Id ne ' + identificationData.ordernumber.value + ')')
          break
      }
    }
  }

  // identificationData.rekvnumber
  if (typeof identificationData.rekvnumber !== 'undefined') {
    arraySearch = shouldPushToArray(makeContains(identificationData.rekvnumber.value, 'RekvNumber', identificationData.rekvnumber.option), arraySearch)
  }

  if (arraySearch.length === 0) {
    return ''
  }
  return '(' + arraySearch.join(' and ') + ')'
}

/**
 *
 * @param {*} props
 * stored datat from this.props
 * @returns obeject that contains source, filter, simpleSearch, hasSimpleSearch
 *
 */
export function getOrderFilterData (props) {
  // console.log('getfilter props', props)
  let filter = { ...props.filter }
  let source = 'orderlist'
  // sourcePage = props.source === undefined ? sourcePage : source
  if (hasAccess([4], props.auth.user.UserType)) {
    source = 'customer'
  } else if (hasAccess([6], props.auth.user.UserType)) {
    source = 'wp'
  } else if (props.source === 'workscreen') {
    source = 'workscreen'
  }

  // get filter from local storage if no filter active/ refresh page
  if (isEqual(props.filter, props.initialFilter)) {
    let storedFilter = JSON.parse(window.localStorage.getItem('filterOrderlist'))
    if (hasAccess([4], props.auth.user.UserType)) {
      storedFilter = JSON.parse(window.localStorage.getItem('filterCustomer'))
    } else if (hasAccess([6], props.auth.user.UserType)) {
      storedFilter = JSON.parse(window.localStorage.getItem('filterWP'))
    } else if (props.source === 'workscreen') {
      storedFilter = JSON.parse(window.localStorage.getItem('filterWorkscreen'))
    }
    if (storedFilter !== undefined && storedFilter !== null && storedFilter.delivery !== undefined) {
      filter = storedFilter
    }
  }

  // Final check to make sure we have a filter
  if (filter === undefined || filter === null || isEqual(filter, { NewOrdersFilter: 0 })) {
    filter = props.initialFilter
  }

  // get simple search from local storage if none active/ page refresh
  let simpleSearch = props.simplesearch
  let storedSimpleSearch = ''
  if (props.source === 'workscreen') {
    storedSimpleSearch = JSON.parse(window.localStorage.getItem('simplesearchWorkscreen'))
    // console.log('getfilter simplesearchWorkscreen', storedSimpleSearch)
  } else {
    storedSimpleSearch = JSON.parse(window.localStorage.getItem('simpleSearch'))
    // console.log('getfilter storedSimpleSearch', storedSimpleSearch)
  }
  if (storedSimpleSearch !== '') {
    simpleSearch = storedSimpleSearch
  }

  let hasSimpleSearch = false
  if (simpleSearch !== undefined && simpleSearch !== null && simpleSearch.value !== undefined && simpleSearch.value !== null && simpleSearch.value.length > 0) {
    hasSimpleSearch = true
  }

  let data = {
    source: source,
    filter: filter,
    simpleSearch: simpleSearch,
    hasSimpleSearch: hasSimpleSearch
  }
  // console.log('getOrderFilterData', data)
  return data
}



// WEBPACK FOOTER //
// ./src/app/utils/filter.js